import { motion } from "framer-motion";
import { ArrowUpRight, Instagram } from "lucide-react";
import { IMAGES, TIKTOK_URL, INSTAGRAM_URL } from "@/constants/site";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const TikTokGlyph = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.6 3c.4 2.2 1.8 3.6 4 3.8v3c-1.6 0-3-.5-4-1.3v6.6c0 3.7-2.6 6-5.9 6-3 0-5.4-2.3-5.4-5.3 0-3.4 3-5.7 6.3-5.2v3.1c-1.5-.5-3.2.4-3.2 2.1 0 1.2 1 2.2 2.3 2.2 1.5 0 2.6-1.1 2.6-2.9V3h3.3z" />
  </svg>
);

export const Gallery = () => (
  <section id="gallery" data-testid="gallery-section" className="relative py-24 md:py-36">
    <div className="mx-auto max-w-7xl px-5 md:px-10">
      <div className="mb-14 flex flex-col gap-6 md:mb-16 md:flex-row md:items-end md:justify-between">
        <div>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mb-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#E60000]"
          >
            <span className="h-px w-10 bg-[#E60000]" />
            The work
          </motion.p>
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl md:text-6xl"
          >
            Before <span className="text-outline">/</span> After
          </motion.h2>
        </div>
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="max-w-sm text-sm leading-relaxed text-zinc-400"
        >
          Real customer transformations drop here as the work rolls in. For the video cuts
          and fresh before/afters, follow along.
        </motion.p>
      </div>

      <div className="grid grid-cols-2 gap-3 [grid-auto-rows:180px] md:grid-cols-4 md:gap-4 [md:grid-auto-rows:220px]">
        {IMAGES.gallery.map((g, i) => (
          <motion.figure
            key={g.src}
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            transition={{ delay: i * 0.05 }}
            className={`group relative overflow-hidden bg-[#0A0A0A] ${g.span}`}
            data-testid={`gallery-item-${i}`}
          >
            <img
              src={g.src}
              alt={g.label}
              loading="lazy"
              className="h-full w-full object-cover transition-[transform,filter] duration-700 ease-out group-hover:scale-105 group-hover:brightness-110"
            />
            <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(5,5,5,0.7),transparent_45%)] opacity-80 transition-opacity duration-500 group-hover:opacity-40" />
            <figcaption className="absolute bottom-0 left-0 flex items-center gap-2 p-4">
              <span className="h-1.5 w-1.5 bg-[#E60000]" aria-hidden="true" />
              <span className="font-display text-[11px] font-semibold uppercase tracking-[0.25em] text-white">
                {g.label}
              </span>
            </figcaption>
          </motion.figure>
        ))}

        {/* Social CTA tile */}
        <motion.a
          href={TIKTOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="gallery-social-tile"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="group relative col-span-2 flex flex-col justify-between overflow-hidden border border-[#E60000]/40 bg-[#0A0A0A] p-6 transition-[background-color,border-color] duration-500 hover:bg-[#140404] md:col-span-2"
        >
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-3 text-white">
              <TikTokGlyph />
              <Instagram size={22} />
            </span>
            <ArrowUpRight
              size={22}
              className="text-[#E60000] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </div>
          <div>
            <div className="font-display text-xl font-bold uppercase leading-tight text-white md:text-2xl">
              @marrmobiledetailing
            </div>
            <div className="mt-1 text-xs uppercase tracking-[0.2em] text-zinc-400">
              Follow the transformations on TikTok & Instagram
            </div>
          </div>
        </motion.a>
      </div>
    </div>
  </section>
);

export default Gallery;
